// Widget belajar Geometri & Pengukuran: petak persegi panjang, bukti luas segitiga/jajargenjang/
// trapesium, kubus satuan, jaring-jaring kubus 3D, tampak bangun, tangga satuan, jam analog,
// perjalanan (jarak = kecepatan × waktu), segitiga J-K-W, busur derajat, sobek sudut segitiga,
// pembulatan di garis bilangan, dan memotong pita (pembagian bersisa).
import { animasi, goyang, letup, titikSVG, seret, batas, jeda, gerakHalus } from '../alat.js';
import { jam } from '../../core/format.js';
import { heightCubes, PIPS } from '../../subjects/matematika/gambar.js';

// ---------- perkakas lokal ----------
const angka = v => Number(Number(v).toPrecision(12)).toLocaleString('id-ID', { maximumFractionDigits: 6 });
const f1 = v => (+v).toFixed(1);

// Pengatur +/− dengan langkah tertentu (pengatur di alat.js selalu ±1).
const atur = (id, label, nilai, satuan = '') =>
  `<div class="pengatur" data-atur="${id}"><span>${label}</span><button type="button" class="btn btn-sm" data-d="-1" aria-label="Kurangi ${label}">−</button><b aria-live="polite">${nilai}</b>${satuan ? `<small class="gm-satuan">${satuan}</small>` : ''}<button type="button" class="btn btn-sm" data-d="1" aria-label="Tambah ${label}">+</button></div>`;
/** cfg: { id: { min, max, langkah } }, nilai: objek keadaan; onUbah(id, nilaiBaru). */
function pasangAtur(host, cfg, nilai, onUbah) {
  host.querySelectorAll('[data-atur]').forEach(el => {
    const id = el.dataset.atur, c = cfg[id];
    if (!c) return;
    el.querySelectorAll('[data-d]').forEach(b => b.addEventListener('click', () => {
      const v = batas(nilai[id] + Number(b.dataset.d) * (c.langkah || 1), c.min, c.max);
      if (v === nilai[id]) return;
      nilai[id] = v;
      el.querySelector('b').textContent = v;
      onUbah(id, v);
    }));
  });
}
const tulisAtur = (host, id, v) => { const b = host.querySelector(`[data-atur="${id}"] b`); if (b) b.textContent = v; };

// Tetapkan transform akhir setelah animasi (juga bila animasi dilewati karena "kurangi gerakan").
async function gerakKe(el, keyframes, opsi) {
  await animasi(el, keyframes, { fill: 'forwards', ...opsi });
  const akhir = keyframes[keyframes.length - 1];
  if (akhir.transform) el.style.transform = akhir.transform;
  if (akhir.opacity !== undefined) el.style.opacity = akhir.opacity;
  el.getAnimations?.().forEach(a => a.cancel());
}

// ---------- 1. Petak persegi panjang: keliling & luas ----------
/**
 * param: { target: { luas?, keliling?, p?, l? }, maks: [kolom, baris], awal: [p, l], satuan }
 */
export function petakPersegiPanjang(host, prm, ctx) {
  const [MP, ML] = prm.maks || [10, 7];
  const st = { p: (prm.awal || [3, 2])[0], l: (prm.awal || [3, 2])[1] };
  const u = prm.satuan || 'cm', K = 30, pad = 14;
  const W = MP * K + pad * 2, H = ML * K + pad * 2;
  host.innerHTML = `<div class="wg gm-petak">
    <svg class="gm-petak-svg" viewBox="0 0 ${W} ${H}" role="img" aria-label="petak persegi panjang">
      <g class="gm-kisi">${Array.from({ length: MP + 1 }, (_, i) => `<line x1="${pad + i * K}" y1="${pad}" x2="${pad + i * K}" y2="${pad + ML * K}"/>`).join('')}${Array.from({ length: ML + 1 }, (_, j) => `<line x1="${pad}" y1="${pad + j * K}" x2="${pad + MP * K}" y2="${pad + j * K}"/>`).join('')}</g>
      <g class="gm-sel"></g>
      <rect class="gm-tepi" x="${pad}" y="${pad}" rx="3"/>
      <circle class="gm-pegangan" r="11" tabindex="0" role="slider" aria-label="Pojok persegi panjang, geser untuk mengubah ukuran"/>
    </svg>
    <div class="wg-alat">${atur('p', 'Panjang', st.p, u)}${atur('l', 'Lebar', st.l, u)}</div>
    <div class="gm-hasil" aria-live="polite"></div>
  </div>`;
  const svg = host.querySelector('svg'), sel = host.querySelector('.gm-sel'), tepi = host.querySelector('.gm-tepi'), peg = host.querySelector('.gm-pegangan'), hasil = host.querySelector('.gm-hasil');
  let beres = false;
  const gambar = () => {
    sel.innerHTML = Array.from({ length: st.p * st.l }, (_, i) => {
      const x = i % st.p, y = Math.floor(i / st.p);
      return `<rect x="${pad + x * K + 2}" y="${pad + y * K + 2}" width="${K - 4}" height="${K - 4}" rx="4" style="animation-delay:${(x + y) * 25}ms"/>${st.p * st.l <= 40 ? `<text x="${pad + x * K + K / 2}" y="${pad + y * K + K / 2 + 4}">${i + 1}</text>` : ''}`;
    }).join('');
    tepi.setAttribute('width', st.p * K); tepi.setAttribute('height', st.l * K);
    peg.setAttribute('cx', pad + st.p * K); peg.setAttribute('cy', pad + st.l * K);
    peg.setAttribute('aria-valuetext', `panjang ${st.p}, lebar ${st.l}`);
    const kel = 2 * (st.p + st.l), luas = st.p * st.l, t = prm.target || {};
    hasil.innerHTML = `<span class="gm-kel${t.keliling ? ' sorot' : ''}">Keliling = 2 × (${st.p} + ${st.l}) = <b>${kel} ${u}</b></span><span class="gm-luas${t.luas ? ' sorot' : ''}">Luas = ${st.p} × ${st.l} = <b>${luas} ${u}²</b></span>`;
    tulisAtur(host, 'p', st.p); tulisAtur(host, 'l', st.l);
  };
  const cek = () => {
    const t = prm.target || {}, kel = 2 * (st.p + st.l), luas = st.p * st.l;
    const ok = (!t.luas || luas === t.luas) && (!t.keliling || kel === t.keliling) && (!t.p || st.p === t.p) && (!t.l || st.l === t.l);
    if (ok) { if (!beres) { beres = true; ctx.selesai(`Pas! Persegi panjang ${st.p} × ${st.l}: kelilingnya <b>${kel} ${u}</b>, luasnya <b>${luas} ${u}²</b>.`); } else ctx.ucap(`Masih benar: ${st.p} × ${st.l}.`, 'cheer'); return; }
    if (t.luas && luas === t.luas && t.keliling) ctx.ucap(`Luasnya sudah ${luas} ${u}², tetapi kelilingnya ${kel} ${u}. Coba ukuran lain yang luasnya tetap ${t.luas}!`, 'think');
    else if (t.luas) ctx.ucap(`Sekarang luasnya ${luas} kotak. Kita perlu ${t.luas} kotak.`, luas > t.luas ? 'wow' : 'think');
    else ctx.ucap(`Kelilingnya ${kel} ${u}. Kita perlu ${t.keliling} ${u}.`, 'think');
  };
  const ubah = (p, l) => {
    p = batas(p, 1, MP); l = batas(l, 1, ML);
    if (p === st.p && l === st.l) return;
    st.p = p; st.l = l; ctx.sfx.tap(); gambar(); cek();
  };
  seret(svg, { onGerak: e => { const pt = titikSVG(svg, e); ubah(Math.round((pt.x - pad) / K), Math.round((pt.y - pad) / K)); } });
  peg.addEventListener('keydown', e => {
    const d = { ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowDown: [0, 1], ArrowUp: [0, -1] }[e.key];
    if (d) { e.preventDefault(); ubah(st.p + d[0], st.l + d[1]); }
  });
  pasangAtur(host, { p: { min: 1, max: MP }, l: { min: 1, max: ML } }, st, () => { ctx.sfx.tap(); gambar(); cek(); });
  gambar();
}

// ---------- 2. Segitiga = setengah persegi panjang ----------
/** param: { alas, tinggi, puncak } (satuan kotak) */
export function segitigaSetengah(host, prm, ctx) {
  const st = { a: prm.alas || 8, t: prm.tinggi || 5, k: prm.puncak ?? 3, terbukti: false };
  const K = 30, pad = 16;
  host.innerHTML = `<div class="wg gm-segi">
    <svg class="gm-bukti" role="img" aria-label="segitiga di dalam persegi panjang"></svg>
    <div class="wg-alat">${atur('a', 'Alas', st.a, 'cm')}${atur('t', 'Tinggi', st.t, 'cm')}<div class="pengatur gm-puncak"><span>Puncak</span><button type="button" class="btn btn-sm" data-pk="-1" aria-label="Geser puncak ke kiri">◀</button><button type="button" class="btn btn-sm" data-pk="1" aria-label="Geser puncak ke kanan">▶</button></div></div>
    <div class="wg-tombol"><button type="button" class="btn btn-go" data-bukti>Buktikan!</button><button type="button" class="btn btn-sm" data-ulang>Ulangi</button></div>
    <div class="gm-hasil" aria-live="polite"></div>
  </div>`;
  const svg = host.querySelector('svg'), hasil = host.querySelector('.gm-hasil');
  const gambar = () => {
    const { a, t, k } = st, W = a * K, H = t * K, X = v => pad + v * K, Y = v => pad + v * K;
    svg.setAttribute('viewBox', `0 0 ${W + pad * 2} ${H + pad * 2 + 22}`);
    const kiri = `${X(0)},${Y(0)} ${X(k)},${Y(0)} ${X(0)},${Y(t)}`, kanan = `${X(k)},${Y(0)} ${X(a)},${Y(0)} ${X(a)},${Y(t)}`;
    svg.innerHTML = `<rect x="${X(0)}" y="${Y(0)}" width="${W}" height="${H}" class="gm-pp" rx="3"/>
      <polygon points="${X(0)},${Y(t)} ${X(a)},${Y(t)} ${X(k)},${Y(0)}" class="gm-segitiga"/>
      <line x1="${X(k)}" y1="${Y(0)}" x2="${X(k)}" y2="${Y(t)}" class="sv-dash"/>
      <g class="gm-potong" data-p="kiri" style="transform-origin:${(X(0) + X(k)) / 2}px ${(Y(0) + Y(t)) / 2}px"><polygon points="${kiri}"/></g>
      <g class="gm-potong" data-p="kanan" style="transform-origin:${(X(k) + X(a)) / 2}px ${(Y(0) + Y(t)) / 2}px"><polygon points="${kanan}"/></g>
      <text x="${X(a / 2)}" y="${Y(t) + 18}" class="sv-t sv-sm">alas ${a} cm</text>`;
    hasil.innerHTML = `<span>Luas persegi panjang = ${a} × ${t} = <b>${a * t} cm²</b></span><span class="sorot">Luas segitiga = ½ × ${a} × ${t} = <b>${angka(a * t / 2)} cm²</b></span>`;
  };
  const bukti = async () => {
    const ps = [...svg.querySelectorAll('.gm-potong')];
    host.querySelector('[data-bukti]').disabled = true;
    ctx.ucap('Perhatikan dua potongan biru di luar segitiga…', 'wow');
    await Promise.all(ps.map((g, i) => gerakKe(g, [{ transform: 'rotate(0deg)' }, { transform: 'rotate(180deg)' }], { duration: 1100, delay: i * 250, easing: 'ease-in-out' })));
    ps.forEach(g => g.classList.add('tutup'));
    host.querySelector('[data-bukti]').disabled = false;
    if (!st.terbukti) { st.terbukti = true; ctx.selesai(`Lihat! Dua potongan biru tepat menutupi segitiga merah. Jadi luas segitiga = <b>setengah</b> luas persegi panjang = ½ × alas × tinggi.`); }
    else ctx.ucap(`Tetap pas! Luas segitiga = ½ × ${st.a} × ${st.t} = ${angka(st.a * st.t / 2)} cm².`, 'cheer');
  };
  host.querySelector('[data-bukti]').addEventListener('click', () => { gambar(); bukti(); });
  host.querySelector('[data-ulang]').addEventListener('click', () => { gambar(); ctx.ucap(''); });
  host.querySelectorAll('[data-pk]').forEach(b => b.addEventListener('click', () => { st.k = batas(st.k + Number(b.dataset.pk), 0, st.a); ctx.sfx.tap(); gambar(); }));
  pasangAtur(host, { a: { min: 2, max: 10 }, t: { min: 2, max: 7 } }, st, () => { st.k = Math.min(st.k, st.a); ctx.sfx.tap(); gambar(); });
  gambar();
}

// ---------- 3. Jajargenjang (potong & geser) dan trapesium (gandakan & putar) ----------
/** param: { mode: 'jajargenjang', alas, tinggi, geser } | { mode: 'trapesium', atas, bawah, tinggi } */
export function bangunGeser(host, prm, ctx) {
  const trap = prm.mode === 'trapesium';
  const K = 30, pad = 16;
  let selesai = false;
  host.innerHTML = `<div class="wg gm-geser">
    <svg class="gm-bukti" role="img" aria-label="${trap ? 'trapesium' : 'jajargenjang'}"></svg>
    <div class="wg-tombol"><button type="button" class="btn btn-go" data-main>${trap ? 'Gandakan & putar' : 'Potong & geser'}</button><button type="button" class="btn btn-sm" data-ulang>Ulangi</button></div>
    <div class="gm-hasil" aria-live="polite"></div>
  </div>`;
  const svg = host.querySelector('svg'), hasil = host.querySelector('.gm-hasil');
  const X = v => pad + v * K, Y = v => pad + v * K;
  const pts = arr => arr.map(([x, y]) => `${f1(X(x))},${f1(Y(y))}`).join(' ');
  const gambar = () => {
    if (!trap) {
      const { alas: a, tinggi: t, geser: g = 2 } = prm;
      svg.setAttribute('viewBox', `0 0 ${(a + g) * K + pad * 2} ${t * K + pad * 2 + 22}`);
      svg.innerHTML = `<polygon points="${pts([[g, 0], [g + a, 0], [a, t], [0, t]])}" class="gm-jajar"/>
        <line x1="${X(g)}" y1="${Y(0)}" x2="${X(g)}" y2="${Y(t)}" class="sv-dash gm-gunting"/>
        <g class="gm-pindah"><polygon points="${pts([[0, t], [g, t], [g, 0]])}" class="gm-jajar potongan"/></g>
        <text x="${X(a / 2)}" y="${Y(t) + 18}" class="sv-t sv-sm">alas ${a} cm</text><text x="${X(g) + 6}" y="${Y(t / 2) + 4}" class="sv-t sv-sm" text-anchor="start">tinggi ${t} cm</text>`;
      hasil.innerHTML = `<span>Luas jajargenjang = alas × tinggi = ${a} × ${t} = <b>${a * t} cm²</b></span>`;
    } else {
      const { atas: a, bawah: b, tinggi: t } = prm, off = (b - a) / 2;
      const M = [(b + off + a) / 2, t / 2];
      // Salinan yang diputar berakhir di x = b + off + a, jadi lebarkan kanvasnya sampai sana.
      svg.setAttribute('viewBox', `0 0 ${(a + b + off) * K + pad * 2} ${t * K + pad * 2 + 22}`);
      const bentuk = pts([[0, t], [b, t], [off + a, 0], [off, 0]]);
      svg.innerHTML = `<polygon points="${bentuk}" class="gm-trap"/>
        <g class="gm-pindah" style="transform-origin:${f1(X(M[0]))}px ${f1(Y(M[1]))}px;opacity:0"><polygon points="${bentuk}" class="gm-trap salinan"/></g>
        <text x="${X(b / 2)}" y="${Y(t) + 18}" class="sv-t sv-sm">${b} cm</text><text x="${X(off + a / 2)}" y="${Y(0) - 4}" class="sv-t sv-sm">${a} cm</text>`;
      hasil.innerHTML = `<span>Luas trapesium = (${a} + ${b}) × ${t} : 2 = <b>${angka((a + b) * t / 2)} cm²</b></span>`;
    }
  };
  host.querySelector('[data-main]').addEventListener('click', async () => {
    gambar();
    const g = svg.querySelector('.gm-pindah'), btn = host.querySelector('[data-main]');
    btn.disabled = true;
    if (!trap) {
      const dx = prm.alas * K;
      ctx.ucap('Potong segitiga di sebelah kiri…', 'wow');
      await jeda(300);
      await gerakKe(g, [{ transform: 'translate(0px,0px)' }, { transform: `translate(${dx / 2}px,-24px)` }, { transform: `translate(${dx}px,0px)` }], { duration: 1200, easing: 'ease-in-out' });
      svg.querySelector('.gm-gunting').classList.add('hilang');
      if (!selesai) { selesai = true; ctx.selesai(`Jadi persegi panjang ${prm.alas} × ${prm.tinggi}! Karena luasnya tidak berubah, luas jajargenjang = <b>alas × tinggi</b>.`); }
    } else {
      const { atas: a, bawah: b, tinggi: t } = prm;
      ctx.ucap('Kita buat kembarannya, lalu putar setengah lingkaran…', 'wow');
      await gerakKe(g, [{ opacity: 0, transform: 'rotate(0deg)' }, { opacity: 1, transform: 'rotate(0deg)' }], { duration: 400 });
      await gerakKe(g, [{ opacity: 1, transform: 'rotate(0deg)' }, { opacity: 1, transform: 'rotate(180deg)' }], { duration: 1300, easing: 'ease-in-out' });
      hasil.innerHTML = `<span>Dua trapesium = jajargenjang dengan alas ${a} + ${b} = ${a + b} cm</span><span class="sorot">Luas 1 trapesium = (${a} + ${b}) × ${t} : 2 = <b>${angka((a + b) * t / 2)} cm²</b></span>`;
      if (!selesai) { selesai = true; ctx.selesai(`Dua trapesium kembar membentuk jajargenjang beralas <b>${a} + ${b}</b>. Satu trapesium adalah setengahnya: (a + b) × t : 2.`); }
    }
    btn.disabled = false;
  });
  host.querySelector('[data-ulang]').addEventListener('click', () => { gambar(); ctx.ucap(''); });
  gambar();
}

// ---------- 4. Kubus satuan: volume balok & kubus ----------
const ISO = 0.866;
const proyeksi = s => (X, Y, Z) => [(X - Y) * ISO * s, (X + Y) * 0.5 * s - Z * s];
function kubusSVG(dims, maks, s, sorotZ = -1, baru = () => false) {
  const P = proyeksi(s), [mp, ml, mt] = maks;
  const sudut = [[0, 0, 0], [mp, 0, 0], [0, ml, 0], [mp, ml, 0], [0, 0, mt], [mp, 0, mt], [0, ml, mt], [mp, ml, mt]].map(c => P(...c));
  const xs = sudut.map(c => c[0]), ys = sudut.map(c => c[1]);
  const vb = [Math.min(...xs) - 10, Math.min(...ys) - 10, Math.max(...xs) - Math.min(...xs) + 20, Math.max(...ys) - Math.min(...ys) + 20];
  const [p, l, t] = dims, kubus = [];
  for (let z = 0; z < t; z++) for (let y = 0; y < l; y++) for (let x = 0; x < p; x++) kubus.push([x, y, z]);
  kubus.sort((A, B) => (A[0] + A[1] + A[2]) - (B[0] + B[1] + B[2]) || A[2] - B[2]);
  const poli = (cls, arr) => `<polygon class="${cls}" points="${arr.map(c => P(...c).map(f1).join(',')).join(' ')}"/>`;
  const isi = kubus.map(([x, y, z]) => `<g class="kb${baru(x, y, z) ? ' jatuh' : ''}${z === sorotZ ? ' sorot' : ''}" style="animation-delay:${z * 160 + (x + y) * 30}ms">${poli('ct', [[x, y, z + 1], [x + 1, y, z + 1], [x + 1, y + 1, z + 1], [x, y + 1, z + 1]])}${poli('cr', [[x + 1, y, z], [x + 1, y + 1, z], [x + 1, y + 1, z + 1], [x + 1, y, z + 1]])}${poli('cl', [[x, y + 1, z], [x + 1, y + 1, z], [x + 1, y + 1, z + 1], [x, y + 1, z + 1]])}</g>`).join('');
  const alas = `<polygon class="gm-lantai" points="${[[0, 0, 0], [mp, 0, 0], [mp, ml, 0], [0, ml, 0]].map(c => P(...c).map(f1).join(',')).join(' ')}"/>`;
  return `<svg class="gm-iso" viewBox="${vb.map(f1).join(' ')}" role="img" aria-label="balok dari ${p * l * t} kubus satuan">${alas}${isi}</svg>`;
}

/** param: { target: { volume, t? }, maks: [p, l, t], awal: [p, l, t], kubus: bool } */
export function kubusSatuan(host, prm, ctx) {
  const maks = prm.maks || [5, 4, 4];
  const st = prm.kubus ? { s: (prm.awal || [2])[0] } : { p: (prm.awal || [2, 2, 1])[0], l: (prm.awal || [2, 2, 1])[1], t: (prm.awal || [2, 2, 1])[2] };
  const dims = () => (prm.kubus ? [st.s, st.s, st.s] : [st.p, st.l, st.t]);
  const ukuran = Math.max(16, Math.min(30, 150 / Math.max(...maks)));
  let lama = dims(), beres = false, sibuk = false;
  host.innerHTML = `<div class="wg gm-volume">
    <div class="gm-iso-wadah"></div>
    <div class="wg-alat">${prm.kubus ? atur('s', 'Rusuk', st.s) : atur('p', 'Panjang', st.p) + atur('l', 'Lebar', st.l) + atur('t', 'Tinggi', st.t)}</div>
    <div class="wg-tombol"><button type="button" class="btn btn-sm btn-sky" data-lapis>Hitung per lapisan</button></div>
    <div class="gm-hasil" aria-live="polite"></div>
  </div>`;
  const wadah = host.querySelector('.gm-iso-wadah'), hasil = host.querySelector('.gm-hasil');
  const rumus = () => {
    const [p, l, t] = dims(), v = p * l * t;
    hasil.innerHTML = prm.kubus
      ? `<span class="sorot">Volume kubus = s × s × s = ${p} × ${p} × ${p} = <b>${v} kubus satuan</b></span>`
      : `<span>Tiap lapisan: ${p} × ${l} = ${p * l} kubus, ada ${t} lapisan</span><span class="sorot">Volume = p × l × t = ${p} × ${l} × ${t} = <b>${v} kubus satuan</b></span>`;
  };
  const gambar = (sorotZ = -1, animasikan = true) => {
    const [p0, l0, t0] = lama;
    const ruang = dims().map((d, i) => Math.min(Math.max(d + 1, 3), prm.kubus ? maks[0] : maks[i]));
    wadah.innerHTML = kubusSVG(dims(), ruang, ukuran, sorotZ, (x, y, z) => animasikan && gerakHalus() && (x >= p0 || y >= l0 || z >= t0));
    lama = dims();
    rumus();
  };
  const cek = () => {
    const [p, l, t] = dims(), v = p * l * t, tg = prm.target || {};
    if (v === tg.volume && (!tg.t || t === tg.t)) {
      if (!beres) { beres = true; ctx.selesai(prm.kubus ? `Kubus dengan rusuk ${p}: ${p} × ${p} × ${p} = <b>${v}</b> kubus satuan!` : `Tepat! ${p} × ${l} × ${t} = <b>${v}</b> kubus satuan.`); }
      else ctx.ucap(`Masih ${v}. Bentuk lain yang volumenya sama juga bisa, lho!`, 'cheer');
    } else if (v === tg.volume) ctx.ucap(`Volumenya sudah ${v}, tetapi tingginya harus ${tg.t}.`, 'think');
    else ctx.ucap(`Sekarang ${v} kubus satuan. Targetnya ${tg.volume}.`, v > tg.volume ? 'wow' : 'think');
  };
  host.querySelector('[data-lapis]').addEventListener('click', async () => {
    if (sibuk) return;
    sibuk = true;
    const [p, l, t] = dims();
    for (let z = 0; z < t; z++) {
      gambar(z, false);
      ctx.ucap(`Lapisan ${z + 1}: ${p} × ${l} = ${p * l} kubus. Jumlah sejauh ini: <b>${p * l * (z + 1)}</b>.`, 'wow');
      ctx.sfx.tap();
      await jeda(900);
    }
    gambar(-1, false);
    sibuk = false;
    cek();
  });
  const cfg = prm.kubus ? { s: { min: 1, max: maks[0] } } : { p: { min: 1, max: maks[0] }, l: { min: 1, max: maks[1] }, t: { min: 1, max: maks[2] } };
  pasangAtur(host, cfg, st, () => { ctx.sfx.tap(); gambar(); cek(); });
  gambar(-1, false);
}

// ---------- 5. Jaring-jaring kubus 3D (dadu) ----------
// Susunan jaring (salib):      [6]
//                              [2]
//                         [3]  [1]  [4]
//                              [5]
// Setelah dilipat: 1 bawah, 6 atas, 2 belakang, 5 depan, 3 kiri, 4 kanan → berhadapan berjumlah 7.
const wajahDadu = n => `<div class="muka">${PIPS[n].map(([x, y]) => `<i style="left:${x * 100}%;top:${y * 100}%"></i>`).join('')}<span class="gm-angka">${n}</span></div><div class="punggung"></div>`;
const LAWAN = { 1: 6, 6: 1, 2: 5, 5: 2, 3: 4, 4: 3 };

/** param: { tanya: angka yang dicari lawannya (bawaan 2) } */
export function jaringKubus(host, prm, ctx) {
  const tanya = prm.tanya || 2, jawab = LAWAN[tanya];
  const st = { lipat: 0, rx: -28, ry: -32 };
  let raf = 0, beres = false;
  host.innerHTML = `<div class="wg gm-jaring">
    <div class="gm-j3-scene" tabindex="0" role="img" aria-label="jaring-jaring kubus, geser untuk memutar">
      <div class="gm-j3-putar">
        <div class="gm-j3-f f1">${wajahDadu(1)}
          <div class="gm-j3-f f2">${wajahDadu(2)}<div class="gm-j3-f f6">${wajahDadu(6)}</div></div>
          <div class="gm-j3-f f5">${wajahDadu(5)}</div>
          <div class="gm-j3-f f3">${wajahDadu(3)}</div>
          <div class="gm-j3-f f4">${wajahDadu(4)}</div>
        </div>
      </div>
    </div>
    <label class="gm-geser-lipat"><span>Lipat</span><input type="range" min="0" max="100" value="0" aria-label="Seberapa terlipat"><b>0%</b></label>
    <div class="wg-tombol"><button type="button" class="btn btn-go" data-lipat>Lipat jadi kubus</button><button type="button" class="btn btn-sm" data-buka>Buka lagi</button><button type="button" class="btn btn-sm" data-putar="-1" aria-label="Putar ke kiri">⟲</button><button type="button" class="btn btn-sm" data-putar="1" aria-label="Putar ke kanan">⟳</button></div>
    <div class="gm-tanya"><p>Angka berapa yang <b>berhadapan</b> dengan <b>${tanya}</b>?</p><div class="gm-pilih">${[1, 2, 3, 4, 5, 6].filter(n => n !== tanya).map(n => `<button type="button" class="btn btn-sm" data-n="${n}">${n}</button>`).join('')}</div></div>
  </div>`;
  const scene = host.querySelector('.gm-j3-scene'), putar = host.querySelector('.gm-j3-putar'), range = host.querySelector('input'), label = host.querySelector('.gm-geser-lipat b');
  const terap = () => {
    putar.style.setProperty('--lipat', `${(st.lipat / 100) * 90}deg`);
    putar.style.setProperty('--rx', `${st.rx}deg`);
    putar.style.setProperty('--ry', `${st.ry}deg`);
    range.value = st.lipat; label.textContent = `${Math.round(st.lipat)}%`;
  };
  const ke = target => {
    cancelAnimationFrame(raf);
    if (!gerakHalus()) { st.lipat = target; terap(); return; }
    const awal = st.lipat, t0 = performance.now(), lama = 1400 * Math.abs(target - awal) / 100;
    const langkah = now => {
      const k = Math.min(1, (now - t0) / Math.max(1, lama)), e = k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2;
      st.lipat = awal + (target - awal) * e; terap();
      if (k < 1) raf = requestAnimationFrame(langkah);
      else if (target === 100) ctx.ucap('Jadi kubus! Putar kubusnya (geser atau tombol ⟲ ⟳) untuk melihat sisi-sisinya.', 'wow');
    };
    raf = requestAnimationFrame(langkah);
  };
  range.addEventListener('input', () => { cancelAnimationFrame(raf); st.lipat = +range.value; terap(); });
  host.querySelector('[data-lipat]').addEventListener('click', () => { ctx.sfx.tap(); ke(100); });
  host.querySelector('[data-buka]').addEventListener('click', () => { ctx.sfx.tap(); ke(0); });
  host.querySelectorAll('[data-putar]').forEach(b => b.addEventListener('click', () => { st.ry += 45 * Number(b.dataset.putar); terap(); }));
  let awalSeret = null;
  seret(scene, {
    onMulai: e => { awalSeret = { x: e.clientX, y: e.clientY, rx: st.rx, ry: st.ry }; },
    onGerak: e => { if (!awalSeret) return; st.ry = awalSeret.ry + (e.clientX - awalSeret.x) * 0.6; st.rx = batas(awalSeret.rx - (e.clientY - awalSeret.y) * 0.6, -85, 85); terap(); },
    onSelesai: () => { awalSeret = null; },
  });
  scene.addEventListener('keydown', e => {
    const d = { ArrowLeft: [0, -15], ArrowRight: [0, 15], ArrowUp: [10, 0], ArrowDown: [-10, 0] }[e.key];
    if (d) { e.preventDefault(); st.rx = batas(st.rx + d[0], -85, 85); st.ry += d[1]; terap(); }
  });
  host.querySelectorAll('[data-n]').forEach(b => b.addEventListener('click', () => {
    const n = +b.dataset.n;
    if (n === jawab) {
      b.classList.add('btn-go');
      host.querySelectorAll('[data-n]').forEach(x => { x.disabled = true; });
      if (!beres) { beres = true; ctx.selesai(`Betul! ${tanya} berhadapan dengan ${jawab}, dan ${tanya} + ${jawab} = <b>7</b>. Pada dadu, sisi yang berhadapan selalu berjumlah 7.`); }
    } else {
      ctx.sfx.no(); goyang(b); b.disabled = true;
      ctx.ucap(st.lipat < 100 ? `Lipat dulu jaring-jaringnya jadi kubus, lalu putar dan cari sisi di seberang angka ${tanya}.` : `${n} bersebelahan dengan ${tanya}, bukan berhadapan. Coba putar kubusnya.`, 'think');
    }
  }));
  terap();
  return () => cancelAnimationFrame(raf);
}

// ---------- 6. Tampak atas & depan dari tumpukan kubus ----------
/** param: { hm: [[tinggi per petak]] (baris belakang → depan), tampak: ['atas', 'depan'] } */
export function tampakBangun(host, prm, ctx) {
  const hm = prm.hm, kolom = hm[0].length, maksT = Math.max(...hm.flat());
  const daftar = prm.tampak || ['atas', 'depan'];
  const kunci = {
    atas: hm.map(r => r.map(h => h > 0)),
    depan: Array.from({ length: maksT }, (_, r) => Array.from({ length: kolom }, (_, x) => Math.max(...hm.map(row => row[x])) >= maksT - r)),
  };
  const st = { i: 0, isi: {}, beres: new Set() };
  daftar.forEach(t => { st.isi[t] = kunci[t].map(r => r.map(() => false)); });
  host.innerHTML = `<div class="wg gm-tampak">
    <div class="gm-tampak-iso">${heightCubes(hm)}</div>
    <div class="segmen gm-tab" role="group" aria-label="Pilih tampak">${daftar.map((t, i) => `<button type="button" data-tab="${i}">Tampak ${t}</button>`).join('')}</div>
    <p class="gm-petunjuk muted"></p>
    <div class="gm-denah"></div>
    <div class="wg-tombol"><button type="button" class="btn btn-sun" data-periksa>Periksa</button><button type="button" class="btn btn-sm" data-hapus>Hapus</button></div>
  </div>`;
  const denah = host.querySelector('.gm-denah'), petunjuk = host.querySelector('.gm-petunjuk');
  const render = () => {
    const t = daftar[st.i], g = st.isi[t];
    host.querySelectorAll('[data-tab]').forEach((b, i) => { b.setAttribute('aria-pressed', String(i === st.i)); b.textContent = `Tampak ${daftar[i]}${st.beres.has(daftar[i]) ? ' ✓' : ''}`; });
    petunjuk.innerHTML = t === 'atas' ? 'Bayangkan kamu melihat dari <b>atas</b>. Ketuk petak yang terlihat. Bagian <b>depan</b> ada di bawah denah.' : 'Bayangkan kamu berdiri di <b>depan</b>. Ketuk kotak yang terlihat, mulai dari bawah.';
    denah.style.setProperty('--kolom', g[0].length);
    denah.innerHTML = g.map((r, y) => r.map((on, x) => `<button type="button" class="gm-kotak${on ? ' on' : ''}" data-x="${x}" data-y="${y}" aria-pressed="${on}" aria-label="baris ${y + 1} kolom ${x + 1}"></button>`).join('')).join('') + `<span class="gm-depan">${t === 'atas' ? 'depan ↓' : 'lantai'}</span>`;
  };
  denah.addEventListener('click', e => {
    const b = e.target.closest('[data-x]');
    if (!b) return;
    const g = st.isi[daftar[st.i]], x = +b.dataset.x, y = +b.dataset.y;
    g[y][x] = !g[y][x]; ctx.sfx.tap(); render();
  });
  host.querySelectorAll('[data-tab]').forEach(b => b.addEventListener('click', () => { st.i = +b.dataset.tab; render(); ctx.ucap(''); }));
  host.querySelector('[data-hapus]').addEventListener('click', () => { const t = daftar[st.i]; st.isi[t] = kunci[t].map(r => r.map(() => false)); render(); });
  host.querySelector('[data-periksa]').addEventListener('click', () => {
    const t = daftar[st.i], g = st.isi[t], k = kunci[t];
    const salah = [];
    k.forEach((r, y) => r.forEach((v, x) => { if (v !== g[y][x]) salah.push([x, y]); }));
    if (!salah.length) {
      st.beres.add(t); ctx.sfx.ok();
      if (st.beres.size === daftar.length) ctx.selesai(`Hebat! Tampak ${daftar.join(' dan ')} semuanya benar.`);
      else { st.i = daftar.findIndex(x => !st.beres.has(x)); ctx.ucap(`Tampak ${t} benar! Sekarang coba tampak ${daftar[st.i]}.`, 'cheer'); }
      render();
    } else {
      ctx.sfx.no(); goyang(denah);
      ctx.ucap(t === 'depan' ? 'Belum tepat. Dari depan, tinggi tiap kolom sama dengan tumpukan <b>tertinggi</b> di kolom itu.' : 'Belum tepat. Dari atas, setiap petak yang ada kubusnya terlihat satu kotak, berapa pun tingginya.', 'think');
      salah.forEach(([x, y]) => denah.querySelector(`[data-x="${x}"][data-y="${y}"]`)?.classList.add('keliru'));
    }
  });
  render();
}

// ---------- 7. Tangga satuan ----------
const TANGGA = {
  panjang: ['km', 'hm', 'dam', 'm', 'dm', 'cm', 'mm'],
  berat: ['kg', 'hg', 'dag', 'g', 'dg', 'cg', 'mg'],
  volume: ['kl', 'hl', 'dal', 'l', 'dl', 'cl', 'ml'],
};
const CATATAN = {
  panjang: '1 km = 1.000 m · 1 m = 100 cm.',
  berat: '1 ton = 1.000 kg · 1 kuintal = 100 kg.',
  volume: '1 liter = 1 dm³ · 1 ml = 1 cm³.',
};

/** param: { jenis: 'panjang'|'berat'|'volume', soal: [{ nilai, dari, ke }] } */
export function tanggaSatuan(host, prm, ctx) {
  const jenis = prm.jenis || 'panjang', tg = TANGGA[jenis];
  const st = { s: 0, idx: 0, nilai: 0, sibuk: false, beres: false };
  host.innerHTML = `<div class="wg gm-tangga">
    <p class="gm-soal" aria-live="polite"></p>
    <div class="gm-anak-tangga">${tg.map((u, i) => `<button type="button" class="gm-step" data-i="${i}" style="--i:${i}" aria-label="Pindah ke ${u}"><b>${u}</b></button>`).join('')}<div class="gm-token" aria-hidden="true"></div></div>
    <div class="wg-tombol"><button type="button" class="btn" data-arah="-1">↖ Naik (: 10)</button><button type="button" class="btn" data-arah="1">Turun ↘ (× 10)</button></div>
    <p class="muted gm-catatan">Turun 1 anak tangga: <b>kali 10</b>. Naik 1 anak tangga: <b>bagi 10</b>. ${CATATAN[jenis]}</p>
  </div>`;
  const token = host.querySelector('.gm-token'), wadah = host.querySelector('.gm-anak-tangga'), soalEl = host.querySelector('.gm-soal');
  const tempat = () => {
    const step = wadah.querySelector(`[data-i="${st.idx}"]`);
    token.style.left = `${step.offsetLeft + step.offsetWidth / 2}px`;
    token.style.top = `${step.offsetTop - 6}px`;
    token.innerHTML = `${angka(st.nilai)} <small>${tg[st.idx]}</small>`;
    wadah.querySelectorAll('.gm-step').forEach((b, i) => b.classList.toggle('kini', i === st.idx));
  };
  const muatSoal = () => {
    const q = prm.soal[st.s];
    st.idx = tg.indexOf(q.dari); st.nilai = q.nilai;
    soalEl.innerHTML = `Ubah <b>${angka(q.nilai)} ${q.dari}</b> menjadi <b>${q.ke}</b>. ${prm.soal.length > 1 ? `<small class="muted">(soal ${st.s + 1} dari ${prm.soal.length})</small>` : ''}`;
    wadah.querySelectorAll('.gm-step').forEach(b => b.classList.toggle('tujuan', b.dataset.i === String(tg.indexOf(q.ke))));
    tempat();
  };
  const label = teks => {
    if (!gerakHalus()) return;
    const s = document.createElement('span');
    s.className = 'gm-terbang'; s.textContent = teks;
    s.style.left = token.style.left; s.style.top = token.style.top;
    wadah.append(s);
    animasi(s, [{ opacity: 0, transform: 'translate(-50%, 0)' }, { opacity: 1, transform: 'translate(-50%, -26px)' }, { opacity: 0, transform: 'translate(-50%, -46px)' }], { duration: 900 }).then(() => s.remove());
  };
  const geser = async arah => {
    const baru = st.idx + arah;
    if (baru < 0 || baru >= tg.length) return;
    st.idx = baru;
    st.nilai = arah > 0 ? st.nilai * 10 : st.nilai / 10;
    ctx.sfx.tap();
    label(arah > 0 ? '× 10' : ': 10');
    tempat();
    letup(token);
    await jeda(380);
  };
  const cek = () => {
    const q = prm.soal[st.s];
    if (tg[st.idx] !== q.ke || st.beres) return;
    const pesan = `${angka(q.nilai)} ${q.dari} = <b>${angka(st.nilai)} ${q.ke}</b>`;
    if (st.s < prm.soal.length - 1) {
      ctx.ucap(`Betul! ${pesan}. Lanjut ke soal berikutnya…`, 'cheer'); ctx.sfx.ok();
      st.s++; st.sibuk = true;
      setTimeout(() => { st.sibuk = false; muatSoal(); }, gerakHalus() ? 1300 : 0);
    } else { st.beres = true; ctx.selesai(`Betul! ${pesan}.`); }
  };
  const ke = async i => {
    if (st.sibuk) return;
    st.sibuk = true;
    while (st.idx !== i) await geser(i > st.idx ? 1 : -1);
    st.sibuk = false;
    cek();
  };
  host.querySelectorAll('[data-arah]').forEach(b => b.addEventListener('click', async () => { if (st.sibuk) return; st.sibuk = true; await geser(Number(b.dataset.arah)); st.sibuk = false; cek(); }));
  wadah.querySelectorAll('.gm-step').forEach(b => b.addEventListener('click', () => ke(+b.dataset.i)));
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(tempat) : null;
  ro?.observe(wadah);
  muatSoal();
  requestAnimationFrame(tempat);
  return () => ro?.disconnect();
}

// ---------- 8. Jam analog ----------
const kataDurasi = m => [Math.floor(m / 60) ? `${Math.floor(m / 60)} jam` : '', m % 60 ? `${m % 60} menit` : ''].filter(Boolean).join(' ') || '0 menit';

/**
 * param: { target: [jam, menit] } atau { mulai: [jam, menit], durasi: menit } (cari waktu selesai),
 *        tampilSudut: tampilkan sudut terkecil antara kedua jarum.
 */
export function jamAnalog(host, prm, ctx) {
  const awal = prm.mulai ? prm.mulai[0] * 60 + prm.mulai[1] : (prm.awal ? prm.awal[0] * 60 + prm.awal[1] : 12 * 60);
  const target = prm.mulai ? awal + prm.durasi : prm.target[0] * 60 + prm.target[1];
  const st = { m: awal };
  let beres = false;
  const C = 110, R = 92;
  const angkaJam = Array.from({ length: 12 }, (_, i) => { const a = ((i + 1) * 30 - 90) * Math.PI / 180; return `<text x="${f1(C + 74 * Math.cos(a))}" y="${f1(C + 74 * Math.sin(a) + 6)}" class="gm-jam-angka">${i + 1}</text>`; }).join('');
  const garis = Array.from({ length: 60 }, (_, i) => { const a = i * 6 * Math.PI / 180, r1 = i % 5 ? R - 6 : R - 12; return `<line x1="${f1(C + r1 * Math.sin(a))}" y1="${f1(C - r1 * Math.cos(a))}" x2="${f1(C + R * Math.sin(a))}" y2="${f1(C - R * Math.cos(a))}" class="${i % 5 ? 'kecil' : ''}"/>`; }).join('');
  host.innerHTML = `<div class="wg gm-jam">
    ${prm.mulai ? `<p class="gm-soal">Mulai pukul <b>${jam(awal)}</b>, lamanya <b>${kataDurasi(prm.durasi)}</b>. Kapan selesai?</p>` : `<p class="gm-soal">Atur jam menjadi pukul <b>${jam(target)}</b>.</p>`}
    <svg class="gm-jam-svg" viewBox="0 0 220 220" tabindex="0" role="slider" aria-label="Jam analog. Panah kiri/kanan: 5 menit, atas/bawah: 1 jam.">
      <circle cx="${C}" cy="${C}" r="${R + 8}" class="gm-jam-muka"/>
      <path class="gm-jam-jejak"/>
      <path class="gm-jam-sudut"/>
      <g class="gm-jam-garis">${garis}</g>${angkaJam}
      <line x1="${C}" y1="${C}" x2="${C}" y2="${C - 48}" class="gm-jarum-jam"/>
      <g class="gm-jarum-menit"><line x1="${C}" y1="${C + 10}" x2="${C}" y2="${C - 78}"/><circle cx="${C}" cy="${C - 78}" r="9" class="gm-pegangan"/></g>
      <circle cx="${C}" cy="${C}" r="6" class="sv-dot"/>
      <text x="${C}" y="${C + 38}" class="gm-jam-sudut-teks"></text>
    </svg>
    <div class="gm-digital" aria-live="polite"></div>
    <div class="wg-tombol"><button type="button" class="btn btn-sm" data-m="-60">−1 jam</button><button type="button" class="btn btn-sm" data-m="-5">−5 menit</button><button type="button" class="btn btn-sm" data-m="5">+5 menit</button><button type="button" class="btn btn-sm" data-m="60">+1 jam</button></div>
  </div>`;
  const svg = host.querySelector('svg'), mnt = host.querySelector('.gm-jarum-menit'), jj = host.querySelector('.gm-jarum-jam'), dig = host.querySelector('.gm-digital');
  const titik = (deg, r) => [C + r * Math.sin(deg * Math.PI / 180), C - r * Math.cos(deg * Math.PI / 180)];
  const busur = (d0, d1, r) => {
    if (d1 - d0 >= 359.9) return `M${C} ${C - r}A${r} ${r} 0 1 1 ${C - 0.01} ${C - r}Z`;
    const [x0, y0] = titik(d0, r), [x1, y1] = titik(d1, r);
    return `M${C} ${C}L${f1(x0)} ${f1(y0)}A${r} ${r} 0 ${d1 - d0 > 180 ? 1 : 0} 1 ${f1(x1)} ${f1(y1)}Z`;
  };
  const gambar = () => {
    mnt.style.transform = `rotate(${st.m * 6}deg)`;
    jj.style.transform = `rotate(${st.m * 0.5}deg)`;
    const lewat = st.m - awal;
    let teks = `<b>${jam(st.m)}</b>`;
    if (prm.mulai) {
      const putaran = Math.floor(Math.max(0, lewat) / 60), sisa = Math.max(0, lewat) % 60;
      host.querySelector('.gm-jam-jejak').setAttribute('d', lewat > 0 ? busur((awal % 60) * 6, (awal % 60) * 6 + (lewat >= 60 && sisa === 0 ? 359.95 : sisa * 6), R - 2) : '');
      teks += `<span>Sudah berlalu: <b>${kataDurasi(Math.max(0, lewat))}</b>${putaran ? ` (${putaran} putaran penuh jarum panjang)` : ''}</span>`;
    }
    if (prm.tampilSudut) {
      const dm = (st.m % 60) * 6, dj = (st.m % 720) * 0.5;
      let a = Math.abs(dm - dj); if (a > 180) a = 360 - a;
      const [d0, d1] = (dm - dj + 360) % 360 <= 180 ? [dj, dj + a] : [dm, dm + a];
      host.querySelector('.gm-jam-sudut').setAttribute('d', a > 0.5 ? busur(d0, d1, 44) : '');
      host.querySelector('.gm-jam-sudut-teks').textContent = `${angka(a)}°`;
      teks += `<span>Sudut terkecil kedua jarum: <b>${angka(a)}°</b></span>`;
    }
    dig.innerHTML = teks;
    svg.setAttribute('aria-valuetext', `pukul ${jam(st.m)}`);
  };
  const cek = () => {
    // Mode durasi harus tepat; mode lain cukup posisi jarumnya sama (jam 03.00 = 15.00 di jam analog).
    const tepat = prm.mulai ? st.m === target : (st.m - target) % 720 === 0;
    if (tepat) {
      if (!beres) { beres = true; ctx.selesai(prm.mulai ? `Tepat! ${jam(awal)} + ${kataDurasi(prm.durasi)} = pukul <b>${jam(target)}</b>.` : prm.tampilSudut ? `Pukul ${jam(target)}: sudutnya ${host.querySelector('.gm-jam-sudut-teks').textContent}. Setiap angka di jam berjarak <b>30°</b>.` : `Betul, pukul <b>${jam(target)}</b>! Jarum pendek menunjuk jam, jarum panjang menunjuk menit.`); }
    }
  };
  const ubah = d => { st.m = Math.max(0, st.m + d); ctx.sfx.tap(); gambar(); cek(); };
  host.querySelectorAll('[data-m]').forEach(b => b.addEventListener('click', () => ubah(Number(b.dataset.m))));
  svg.addEventListener('keydown', e => {
    const d = { ArrowRight: 5, ArrowLeft: -5, ArrowUp: 60, ArrowDown: -60 }[e.key];
    if (d) { e.preventDefault(); ubah(d); }
  });
  // Seret jarum menit: menit mengikuti sudut pointer (kelipatan 5); melewati angka 12 menambah/mengurangi jam.
  seret(svg, {
    onGerak: e => {
      const pt = titikSVG(svg, e);
      let deg = Math.atan2(pt.x - C, C - pt.y) * 180 / Math.PI; if (deg < 0) deg += 360;
      const menitBaru = (Math.round(deg / 30) * 5) % 60, menitLama = st.m % 60;
      let d = menitBaru - menitLama;
      if (d > 30) d -= 60; if (d < -30) d += 60;
      if (d) { st.m = Math.max(0, st.m + d); gambar(); }
    },
    onSelesai: () => { ctx.sfx.tap(); cek(); },
  });
  gambar();
}

// ---------- 9. Perjalanan: jarak = kecepatan × waktu ----------
/** param: { target: jarak km } */
export function perjalanan(host, prm, ctx) {
  const st = { k: prm.kecepatan || 40, w: prm.waktu || 2, sibuk: false };
  const T = prm.target || 150;
  let beres = false;
  host.innerHTML = `<div class="wg gm-jalan">
    <p class="gm-soal">Kota tujuan berjarak <b>${T} km</b>. Atur kecepatan dan waktunya, lalu tekan <b>Jalan!</b></p>
    <svg class="gm-jalan-svg" viewBox="0 0 520 120" role="img" aria-label="jalan dan mobil">
      <rect x="0" y="54" width="520" height="34" rx="8" class="gm-aspal"/>
      <line x1="10" y1="71" x2="510" y2="71" class="gm-marka"/>
      <g class="gm-penanda"></g>
      <g class="gm-bendera"></g>
      <g class="gm-mobil"><rect x="-26" y="-20" width="52" height="18" rx="6" class="gm-mobil-badan"/><path d="M-16 -20 L-10 -32 H10 L18 -20Z" class="gm-mobil-kaca"/><circle cx="-14" cy="0" r="6" class="gm-roda"/><circle cx="14" cy="0" r="6" class="gm-roda"/></g>
    </svg>
    <div class="wg-alat">${atur('k', 'Kecepatan', st.k, 'km/jam')}${atur('w', 'Waktu', st.w, 'jam')}</div>
    <div class="wg-tombol"><button type="button" class="btn btn-go" data-jalan>Jalan!</button></div>
    <div class="gm-hasil" aria-live="polite"></div>
  </div>`;
  const mobil = host.querySelector('.gm-mobil'), penanda = host.querySelector('.gm-penanda'), hasil = host.querySelector('.gm-hasil');
  const x0 = 30, x1 = 490;
  const skala = () => (x1 - x0) / Math.max(T * 1.25, st.k * st.w);
  const tulis = () => { hasil.innerHTML = `<span class="sorot">Jarak = kecepatan × waktu = ${st.k} × ${st.w} = <b>${st.k * st.w} km</b></span>`; };
  const siapkan = () => {
    const s = skala();
    host.querySelector('.gm-bendera').innerHTML = `<line x1="${x0 + T * s}" y1="20" x2="${x0 + T * s}" y2="90" class="gm-tiang"/><path d="M${x0 + T * s} 20 h22 l-6 7 l6 7 h-22Z" class="gm-kain"/><text x="${x0 + T * s}" y="110" class="sv-t sv-sm">${T} km</text>`;
    penanda.innerHTML = '';
    mobil.style.transform = `translate(${x0}px, 64px)`;
    tulis();
  };
  host.querySelector('[data-jalan]').addEventListener('click', async () => {
    if (st.sibuk) return;
    st.sibuk = true;
    const btn = host.querySelector('[data-jalan]'); btn.disabled = true;
    siapkan();
    const s = skala(), J = st.k * st.w;
    ctx.ucap(`Brum! Setiap 1 jam mobil menempuh <b>${st.k} km</b>.`, 'wow');
    for (let i = 1; i <= st.w; i++) {
      await gerakKe(mobil, [{ transform: `translate(${x0 + st.k * (i - 1) * s}px, 64px)` }, { transform: `translate(${x0 + st.k * i * s}px, 64px)` }], { duration: 700, easing: 'linear' });
      penanda.insertAdjacentHTML('beforeend', `<g class="gm-jam-tanda"><line x1="${x0 + st.k * i * s}" y1="46" x2="${x0 + st.k * i * s}" y2="96" class="sv-dash"/><text x="${x0 + st.k * i * s}" y="40" class="sv-t sv-sm">${i} jam: ${st.k * i} km</text></g>`);
      ctx.sfx.tap();
    }
    btn.disabled = false; st.sibuk = false;
    if (J === T) { if (!beres) { beres = true; ctx.selesai(`Sampai tepat di kota tujuan! ${st.k} km/jam × ${st.w} jam = <b>${J} km</b>.`); } }
    else ctx.ucap(J < T ? `Baru ${J} km, belum sampai. Kurang ${T - J} km.` : `Kelewatan! ${J} km lebih jauh ${J - T} km dari tujuan.`, J < T ? 'think' : 'wow');
  });
  pasangAtur(host, { k: { min: 10, max: 100, langkah: 5 }, w: { min: 1, max: 5 } }, st, () => { ctx.sfx.tap(); siapkan(); });
  siapkan();
}

// ---------- 10. Segitiga rumus J-K-W ----------
/** param: { contoh: { J, K, W } } */
export function segitigaJKW(host, prm, ctx) {
  const c = prm.contoh || { J: 120, K: 40, W: 3 };
  const RUMUS = {
    J: { nama: 'Jarak', rumus: 'J = K × W', contoh: `${c.K} × ${c.W} = ${c.J} km` },
    K: { nama: 'Kecepatan', rumus: 'K = J : W', contoh: `${c.J} : ${c.W} = ${c.K} km/jam` },
    W: { nama: 'Waktu', rumus: 'W = J : K', contoh: `${c.J} : ${c.K} = ${c.W} jam` },
  };
  const dicoba = new Set();
  host.innerHTML = `<div class="wg gm-jkw">
    <div class="gm-jkw-segi">
      <button type="button" class="gm-jkw-b atas" data-h="J" aria-label="Tutup J (jarak)"><b>J</b><small>jarak</small></button>
      <button type="button" class="gm-jkw-b kiri" data-h="K" aria-label="Tutup K (kecepatan)"><b>K</b><small>kecepatan</small></button>
      <span class="gm-jkw-kali">×</span>
      <button type="button" class="gm-jkw-b kanan" data-h="W" aria-label="Tutup W (waktu)"><b>W</b><small>waktu</small></button>
    </div>
    <div class="gm-jkw-hasil" aria-live="polite"><span class="muted">Ketuk huruf yang ingin dicari. Huruf itu ditutup, sisanya menunjukkan rumusnya.</span></div>
  </div>`;
  const hasil = host.querySelector('.gm-jkw-hasil');
  host.querySelectorAll('[data-h]').forEach(b => b.addEventListener('click', () => {
    const h = b.dataset.h, r = RUMUS[h];
    host.querySelectorAll('[data-h]').forEach(x => x.classList.toggle('tutup', x === b));
    hasil.innerHTML = `<b class="gm-jkw-rumus">${r.rumus}</b><span>Contoh: ${r.nama} = ${r.contoh}</span>`;
    letup(hasil.firstElementChild);
    ctx.sfx.tap(); dicoba.add(h);
    if (dicoba.size === 3) ctx.selesai('Kamu sudah tahu ketiga rumusnya: J = K × W, K = J : W, dan W = J : K.');
    else ctx.ucap(`${h === 'J' ? 'Jarak: K dan W berdampingan, jadi dikali.' : `${r.nama}: J di atas, jadi dibagi.`} Coba huruf lainnya!`, 'wow');
  }));
}

// ---------- 11. Busur derajat ----------
/** param: { target: [derajat, …] } atau { mode: 'lurus', pelurus: derajat sudut ungu } */
export function busurSudut(host, prm, ctx) {
  const lurus = prm.mode === 'lurus';
  const target = lurus ? [180 - prm.pelurus] : prm.target || [90];
  const st = { a: lurus ? 90 : 40, i: 0 };
  let beres = false;
  const C = [160, 170], R = 140;
  const jenis = a => (a === 0 ? 'nol' : a < 90 ? 'lancip' : a === 90 ? 'siku-siku' : a < 180 ? 'tumpul' : 'lurus');
  const titik = (deg, r) => [C[0] + r * Math.cos(deg * Math.PI / 180), C[1] - r * Math.sin(deg * Math.PI / 180)];
  const garisSkala = Array.from({ length: 37 }, (_, i) => { const d = i * 5, [x0, y0] = titik(d, R - (d % 10 ? 8 : 14)), [x1, y1] = titik(d, R); return `<line x1="${f1(x0)}" y1="${f1(y0)}" x2="${f1(x1)}" y2="${f1(y1)}"/>`; }).join('')
    + Array.from({ length: 7 }, (_, i) => { const d = i * 30, [x, y] = titik(d, R - 26); return `<text x="${f1(x)}" y="${f1(y + 4)}">${d}</text>`; }).join('');
  host.innerHTML = `<div class="wg gm-busur">
    <p class="gm-soal" aria-live="polite"></p>
    <svg class="gm-busur-svg" viewBox="0 0 320 196" tabindex="0" role="slider" aria-valuemin="0" aria-valuemax="180" aria-label="Sudut. Panah kiri/kanan mengubah 1 derajat, atas/bawah 5 derajat.">
      <path d="M${C[0] - R} ${C[1]} A${R} ${R} 0 0 1 ${C[0] + R} ${C[1]} Z" class="gm-busur-badan"/>
      <g class="gm-busur-skala">${garisSkala}</g>
      <path class="gm-busur-a"/><path class="gm-busur-b"/>
      <line x1="${lurus ? C[0] - R : C[0]}" y1="${C[1]}" x2="${C[0] + R}" y2="${C[1]}" class="gm-sinar"/>
      <line x1="${C[0]}" y1="${C[1]}" class="gm-sinar gerak"/>
      <circle r="11" class="gm-pegangan"/>
      <text class="gm-busur-teks a"></text><text class="gm-busur-teks b"></text>
      <circle cx="${C[0]}" cy="${C[1]}" r="5" class="sv-dot"/>
    </svg>
    <div class="gm-digital" aria-live="polite"></div>
    <div class="wg-tombol"><button type="button" class="btn btn-sm" data-d="-5">−5°</button><button type="button" class="btn btn-sm" data-d="-1">−1°</button><button type="button" class="btn btn-sm" data-d="1">+1°</button><button type="button" class="btn btn-sm" data-d="5">+5°</button></div>
  </div>`;
  const svg = host.querySelector('svg'), soal = host.querySelector('.gm-soal'), dig = host.querySelector('.gm-digital');
  const sektor = (d0, d1, r) => { const [x0, y0] = titik(d0, r), [x1, y1] = titik(d1, r); return `M${C[0]} ${C[1]}L${f1(x0)} ${f1(y0)}A${r} ${r} 0 0 0 ${f1(x1)} ${f1(y1)}Z`; };
  const tulisSoal = () => {
    soal.innerHTML = lurus ? `Garis lurus = <b>180°</b>. Atur sinar supaya sudut <b class="ungu">ungu</b> menjadi <b>${prm.pelurus}°</b>.`
      : `Buat sudut <b>${target[st.i]}°</b> (${jenis(target[st.i])}).${target.length > 1 ? ` <small class="muted">(${st.i + 1} dari ${target.length})</small>` : ''}`;
  };
  const gambar = () => {
    const a = st.a, [ex, ey] = titik(a, R - 4), [hx, hy] = titik(a, R - 30);
    const g = svg.querySelector('.gerak'); g.setAttribute('x2', f1(ex)); g.setAttribute('y2', f1(ey));
    const p = svg.querySelector('.gm-pegangan'); p.setAttribute('cx', f1(hx)); p.setAttribute('cy', f1(hy));
    svg.querySelector('.gm-busur-a').setAttribute('d', a > 0 ? sektor(0, a, 46) : '');
    svg.querySelector('.gm-busur-b').setAttribute('d', lurus && a < 180 ? sektor(a, 180, 34) : '');
    const [ta, tb] = [titik(a / 2, 62), titik(a / 2, 62)];
    const tA = svg.querySelector('.gm-busur-teks.a'); tA.setAttribute('x', f1(ta[0])); tA.setAttribute('y', f1(tb[1] + 5)); tA.textContent = `${a}°`;
    const tB = svg.querySelector('.gm-busur-teks.b');
    if (lurus) { const [bx, by] = titik((a + 180) / 2, 54); tB.setAttribute('x', f1(bx)); tB.setAttribute('y', f1(by + 5)); tB.textContent = `${180 - a}°`; } else tB.textContent = '';
    dig.innerHTML = lurus ? `<span class="gm-jumlah"><b>${a}°</b> + <b class="ungu">${180 - a}°</b> = <b>180°</b></span>` : `<b>${a}°</b> <span class="gm-jenis">sudut ${jenis(a)}</span>`;
    svg.setAttribute('aria-valuenow', a);
  };
  const cek = () => {
    if (st.a !== target[st.i] || beres) return;
    if (st.i < target.length - 1) { ctx.ucap(`Tepat, ${st.a}° adalah sudut ${jenis(st.a)}! Sekarang buat ${target[st.i + 1]}°.`, 'cheer'); ctx.sfx.ok(); st.i++; tulisSoal(); }
    else { beres = true; ctx.selesai(lurus ? `Betul! ${st.a}° + ${prm.pelurus}° = 180°. Sudut pada garis lurus selalu berjumlah <b>180°</b>.` : `Tepat ${st.a}°, sudut ${jenis(st.a)}!`); }
  };
  const ubah = d => { st.a = batas(st.a + d, 0, 180); gambar(); cek(); };
  host.querySelectorAll('[data-d]').forEach(b => b.addEventListener('click', () => { ctx.sfx.tap(); ubah(Number(b.dataset.d)); }));
  svg.addEventListener('keydown', e => {
    const d = { ArrowRight: -1, ArrowLeft: 1, ArrowUp: 5, ArrowDown: -5 }[e.key];
    if (d) { e.preventDefault(); ubah(d); }
  });
  seret(svg, {
    onGerak: e => {
      const pt = titikSVG(svg, e);
      let deg = Math.atan2(C[1] - pt.y, pt.x - C[0]) * 180 / Math.PI;
      if (deg < -90) deg = 180; if (deg < 0) deg = 0;
      const baru = batas(Math.round(deg / 5) * 5, 0, 180);
      if (baru !== st.a) { st.a = baru; gambar(); }
    },
    onSelesai: () => { ctx.sfx.tap(); cek(); },
  });
  tulisSoal(); gambar();
}

// ---------- 12. Sobek sudut segitiga ----------
const BENTUK_SEGITIGA = [[60, 70], [40, 95], [90, 35], [55, 55]];
/** param: { bentuk: [[A, B], …] } – sudut alas kiri & kanan */
export function sobekSegitiga(host, prm, ctx) {
  const bentuk = prm.bentuk || BENTUK_SEGITIGA;
  const st = { i: 0, sibuk: false };
  let beres = false;
  host.innerHTML = `<div class="wg gm-sobek">
    <svg class="gm-sobek-svg" viewBox="0 0 320 250" role="img" aria-label="segitiga dan sudut-sudutnya"></svg>
    <div class="wg-tombol"><button type="button" class="btn btn-go" data-sobek>Sobek & susun</button><button type="button" class="btn btn-sm" data-ganti>Ganti segitiga</button></div>
    <div class="gm-digital" aria-live="polite"></div>
  </div>`;
  const svg = host.querySelector('svg'), dig = host.querySelector('.gm-digital');
  const geom = () => {
    const [A, B] = bentuk[st.i], C = 180 - A - B, W = 220;
    const a = A * Math.PI / 180, b = B * Math.PI / 180;
    let x, y;
    if (A === 90) { x = 0; y = W * Math.tan(b); } else if (B === 90) { x = W; y = W * Math.tan(a); } else { x = W * Math.tan(b) / (Math.tan(a) + Math.tan(b)); y = x * Math.tan(a); }
    const k = y > 130 ? 130 / y : 1, Wd = W * k, x0 = (320 - Wd) / 2, yb = 150;
    return { A, B, C, PA: [x0, yb], PB: [x0 + Wd, yb], PC: [x0 + x * k, yb - y * k], P: [160, 228] };
  };
  // Sektor sudut di titik V, arah (matematika, berlawanan jarum jam) dari s ke e derajat.
  const sektor = (V, s, e, r) => {
    const p = d => [V[0] + r * Math.cos(d * Math.PI / 180), V[1] - r * Math.sin(d * Math.PI / 180)];
    const [x0, y0] = p(s), [x1, y1] = p(e);
    return `M${f1(V[0])} ${f1(V[1])}L${f1(x0)} ${f1(y0)}A${r} ${r} 0 ${e - s > 180 ? 1 : 0} 0 ${f1(x1)} ${f1(y1)}Z`;
  };
  const gambar = () => {
    const g = geom(), r = 36;
    const sudut = [
      { n: 'A', V: g.PA, s: 0, e: g.A, rot: 180 - g.A, cls: 'pink' },
      { n: 'B', V: g.PB, s: 180 - g.B, e: 180, rot: g.B - 180, cls: 'sky' },
      { n: 'C', V: g.PC, s: 180 + g.A, e: 360 - g.B, rot: g.B - 180 - g.A, cls: 'sun' },
    ];
    svg.innerHTML = `<polygon points="${[g.PA, g.PB, g.PC].map(p => p.map(f1).join(',')).join(' ')}" class="gm-segi-sobek"/>
      <line x1="40" y1="${g.P[1]}" x2="280" y2="${g.P[1]}" class="sv-line gm-garis-lurus"/>
      ${sudut.map(s => `<g class="gm-irisan ${s.cls}" data-rot="${s.rot}" data-dx="${f1(g.P[0] - s.V[0])}" data-dy="${f1(g.P[1] - s.V[1])}" style="transform-origin:${f1(s.V[0])}px ${f1(s.V[1])}px"><path d="${sektor(s.V, s.s, s.e, r)}"/></g>`).join('')}
      <text x="${f1(g.PA[0] + 40)}" y="${f1(g.PA[1] - 8)}" class="sv-t sv-sm">${g.A}°</text><text x="${f1(g.PB[0] - 40)}" y="${f1(g.PB[1] - 8)}" class="sv-t sv-sm">${g.B}°</text><text x="${f1(g.PC[0])}" y="${f1(g.PC[1] + 46)}" class="sv-t sv-sm">${g.C}°</text>
      <text x="160" y="246" class="sv-t sv-sm gm-180" opacity="0">${g.A}° + ${g.B}° + ${g.C}° = 180° (garis lurus)</text>`;
    dig.innerHTML = `<span class="gm-jumlah">Sudut: <b class="pink">${g.A}°</b>, <b class="sky">${g.B}°</b>, <b class="sun">${g.C}°</b></span>`;
  };
  host.querySelector('[data-sobek]').addEventListener('click', async () => {
    if (st.sibuk) return;
    st.sibuk = true; gambar();
    const btn = host.querySelector('[data-sobek]'); btn.disabled = true;
    ctx.ucap('Kita sobek ketiga pojoknya, lalu susun berjajar di garis…', 'wow');
    const irisan = [...svg.querySelectorAll('.gm-irisan')];
    for (const [i, el] of irisan.entries()) {
      await gerakKe(el, [{ transform: 'translate(0px,0px) rotate(0deg)' }, { transform: `translate(${el.dataset.dx}px,${el.dataset.dy}px) rotate(${-Number(el.dataset.rot)}deg)` }], { duration: 900, delay: i ? 120 : 0, easing: 'ease-in-out' });
      ctx.sfx.tap();
    }
    svg.querySelector('.gm-180').setAttribute('opacity', '1');
    const g = geom();
    dig.innerHTML = `<span class="gm-jumlah"><b class="pink">${g.A}°</b> + <b class="sky">${g.B}°</b> + <b class="sun">${g.C}°</b> = <b>180°</b></span>`;
    btn.disabled = false; st.sibuk = false;
    if (!beres) { beres = true; ctx.selesai('Ketiga sudut tepat membentuk <b>garis lurus</b>. Jadi jumlah sudut segitiga selalu <b>180°</b>. Coba ganti segitiganya!'); }
    else ctx.ucap(`Segitiga lain pun sama: jumlahnya 180°.`, 'cheer');
  });
  host.querySelector('[data-ganti]').addEventListener('click', () => { if (st.sibuk) return; st.i = (st.i + 1) % bentuk.length; ctx.sfx.tap(); gambar(); ctx.ucap(''); });
  gambar();
}

// ---------- 13. Pembulatan di garis bilangan ----------
/** param: { ke: 10|100|1000, soal: [angka, …] } */
export function pembulatan(host, prm, ctx) {
  const ke = prm.ke || 10, soal = prm.soal;
  const st = { i: 0, sibuk: false };
  host.innerHTML = `<div class="wg gm-bulat">
    <p class="gm-soal" aria-live="polite"></p>
    <svg class="gm-bulat-svg" viewBox="0 0 340 110" role="img" aria-label="garis bilangan"></svg>
    <div class="wg-tombol gm-bulat-pilih"></div>
    <p class="muted gm-catatan">Angka yang tepat di <b>tengah</b> atau lewat tengah dibulatkan <b>ke atas</b>.</p>
  </div>`;
  const svg = host.querySelector('svg'), pilih = host.querySelector('.gm-bulat-pilih'), soalEl = host.querySelector('.gm-soal');
  const x0 = 30, x1 = 310;
  const render = () => {
    const n = soal[st.i], lo = Math.floor(n / ke) * ke, hi = lo + ke, X = v => x0 + (v - lo) / ke * (x1 - x0);
    const langkah = ke / 10;
    soalEl.innerHTML = `Bulatkan <b>${angka(n)}</b> ke ${ke === 10 ? 'puluhan' : ke === 100 ? 'ratusan' : 'ribuan'} terdekat. <small class="muted">(${st.i + 1} dari ${soal.length})</small>`;
    svg.innerHTML = `<line x1="${x0}" y1="60" x2="${x1}" y2="60" class="sv-axis"/>
      ${Array.from({ length: 11 }, (_, k) => `<line x1="${X(lo + k * langkah)}" y1="${k === 5 ? 48 : 54}" x2="${X(lo + k * langkah)}" y2="${k === 5 ? 72 : 66}" class="${k === 5 ? 'gm-tengah' : 'sv-axis'}"/>`).join('')}
      <text x="${X(lo)}" y="92" class="sv-t">${angka(lo)}</text><text x="${X(hi)}" y="92" class="sv-t">${angka(hi)}</text><text x="${X(lo + ke / 2)}" y="92" class="sv-t sv-sm gm-tengah-t">tengah ${angka(lo + ke / 2)}</text>
      <g class="gm-pin" style="transform:translate(${f1(X(n))}px,0)"><path d="M0 56 l-9 -16 h18Z"/><text x="0" y="32" class="sv-t">${angka(n)}</text></g>`;
    pilih.innerHTML = `<button type="button" class="btn" data-v="${lo}">← ${angka(lo)}</button><button type="button" class="btn" data-v="${hi}">${angka(hi)} →</button>`;
    animasi(svg.querySelector('.gm-pin'), [{ transform: `translate(${x0}px,-10px)`, opacity: 0 }, { transform: `translate(${f1(X(n))}px,0)`, opacity: 1 }], { duration: 500 });
    pilih.querySelectorAll('[data-v]').forEach(b => b.addEventListener('click', () => jawab(+b.dataset.v, X)));
  };
  const jawab = async (v, X) => {
    if (st.sibuk) return;
    const n = soal[st.i], lo = Math.floor(n / ke) * ke, benar = n - lo >= ke / 2 ? lo + ke : lo;
    const pin = svg.querySelector('.gm-pin');
    if (v === benar) {
      st.sibuk = true; ctx.sfx.ok();
      await gerakKe(pin, [{ transform: `translate(${f1(X(n))}px,0)` }, { transform: `translate(${f1(X(v))}px,0)` }], { duration: 600 });
      const alasan = n - lo >= ke / 2 ? `${angka(n)} sudah lewat tengah (${angka(lo + ke / 2)})` : `${angka(n)} belum sampai tengah (${angka(lo + ke / 2)})`;
      if (st.i < soal.length - 1) { ctx.ucap(`Betul! ${alasan}, jadi dibulatkan menjadi <b>${angka(v)}</b>.`, 'cheer'); st.i++; await jeda(900); st.sibuk = false; render(); }
      else ctx.selesai(`Betul! ${alasan}, jadi dibulatkan menjadi <b>${angka(v)}</b>.`);
    } else {
      ctx.sfx.no(); goyang(pin.closest('svg'));
      ctx.ucap(`Lihat garis <b>tengah</b> (${angka(lo + ke / 2)}). ${angka(n)} lebih dekat ke mana?`, 'think');
    }
  };
  render();
}

// ---------- 14. Memotong pita: pembagian bersisa ----------
/** param: { panjang: cm, potong: cm } */
export function potongPita(host, prm, ctx) {
  const L = prm.panjang, p = prm.potong, n = Math.floor(L / p), sisa = L - n * p;
  const st = { dipotong: 0, sibuk: false };
  const X0 = 10, X1 = 330, s = (X1 - X0) / L;
  host.innerHTML = `<div class="wg gm-pita">
    <p class="gm-soal">Pita <b>${L} cm</b> dipotong-potong, tiap potong <b>${p} cm</b>.</p>
    <svg class="gm-pita-svg" viewBox="0 0 340 150" role="img" aria-label="pita yang dipotong">
      <g class="gm-penggaris">${Array.from({ length: Math.floor(L / 10) + 1 }, (_, i) => `<line x1="${f1(X0 + i * 10 * s)}" y1="8" x2="${f1(X0 + i * 10 * s)}" y2="${i % 5 ? 14 : 18}"/>`).join('')}<text x="${X0}" y="30" class="sv-t sv-sm" text-anchor="start">0</text><text x="${X1}" y="30" class="sv-t sv-sm" text-anchor="end">${L} cm</text></g>
      <rect class="gm-pita-sisa" x="${X0}" y="38" width="${f1(L * s)}" height="26" rx="5"/>
      <g class="gm-potongan"></g>
    </svg>
    <div class="wg-tombol"><button type="button" class="btn btn-go" data-potong>✂ Potong ${p} cm</button><button type="button" class="btn btn-sm" data-semua>Potong semua</button></div>
    <div class="gm-hasil" aria-live="polite"></div>
  </div>`;
  const svg = host.querySelector('svg'), sisaEl = svg.querySelector('.gm-pita-sisa'), wadah = svg.querySelector('.gm-potongan'), hasil = host.querySelector('.gm-hasil');
  const tulis = () => { hasil.innerHTML = `<span>Potongan: <b>${st.dipotong}</b> · Sisa pita: <b>${L - st.dipotong * p} cm</b></span>`; };
  const potong = async () => {
    if (st.sibuk || st.dipotong >= n) return;
    st.sibuk = true;
    const i = st.dipotong, kol = i % 8, brs = Math.floor(i / 8);
    const lebar = p * s, gx = X0 + i * lebar;
    st.dipotong++;
    sisaEl.setAttribute('x', f1(X0 + st.dipotong * lebar)); sisaEl.setAttribute('width', f1((L - st.dipotong * p) * s));
    const tx = 6 + kol * 41, ty = 84 + brs * 30, skala = Math.min(1, 36 / lebar);
    wadah.insertAdjacentHTML('beforeend', `<g class="gm-sepotong" style="transform-origin:${f1(gx)}px 38px"><rect x="${f1(gx)}" y="38" width="${f1(lebar)}" height="26" rx="5"/><text x="${f1(gx + lebar / 2)}" y="56" class="sv-t sv-sm">${p}</text></g>`);
    const g = wadah.lastElementChild;
    ctx.sfx.tap();
    await gerakKe(g, [{ transform: 'translate(0px,0px) scale(1)' }, { transform: `translate(${f1(tx - gx)}px,${ty - 38}px) scale(${skala.toFixed(3)})` }], { duration: 450, easing: 'ease-out' });
    tulis();
    st.sibuk = false;
    if (st.dipotong === n) {
      host.querySelector('[data-potong]').disabled = true; host.querySelector('[data-semua]').disabled = true;
      ctx.selesai(sisa ? `Sisa <b>${sisa} cm</b> tidak cukup untuk satu potong ${p} cm. Jadi ${L} : ${p} = <b>${n} sisa ${sisa}</b>.` : `Pas, tidak ada sisa! ${L} : ${p} = <b>${n}</b>.`);
    } else ctx.ucap(`${st.dipotong} potong. Masih ${L - st.dipotong * p} cm.`);
  };
  host.querySelector('[data-potong]').addEventListener('click', potong);
  host.querySelector('[data-semua]').addEventListener('click', async () => { while (st.dipotong < n) { await potong(); await jeda(80); } });
  tulis();
}

export const WIDGET_GEOMETRI = {
  petakPersegiPanjang, segitigaSetengah, bangunGeser, kubusSatuan, jaringKubus, tampakBangun,
  tanggaSatuan, jamAnalog, perjalanan, segitigaJKW, busurSudut, sobekSegitiga, pembulatan, potongPita,
};
